import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface BreathingActivityProps {
  emotion: string;
  onComplete: () => void;
}

const BreathingActivity: React.FC<BreathingActivityProps> = ({ emotion, onComplete }) => {
  const navigate = useNavigate();
  const [isActive, setIsActive] = useState(false);
  const [phase, setPhase] = useState<'inhale' | 'hold' | 'exhale'>('inhale');
  const [count, setCount] = useState(4);
  const [cycle, setCycle] = useState(1);
  const [isCompleted, setIsCompleted] = useState(false);

  const phaseDurations = { inhale: 4, hold: 7, exhale: 8 };
  const totalCycles = 5;

  useEffect(() => {
    let interval: number;
    if (isActive && cycle <= totalCycles) {
      interval = window.setInterval(() => {
        setCount(prev => {
          if (prev > 1) {
            return prev - 1;
          } else {
            // 다음 단계로 이동
            if (phase === 'inhale') {
              setPhase('hold');
              return phaseDurations.hold;
            } else if (phase === 'hold') {
              setPhase('exhale');
              return phaseDurations.exhale;
            } else {
              // 사이클 완료
              if (cycle < totalCycles) {
                setCycle(prev => prev + 1);
                setPhase('inhale');
                return phaseDurations.inhale;
              } else {
                // 모든 사이클 완료
                setIsActive(false);
                setIsCompleted(true);
                setTimeout(() => {
                  onComplete();
                }, 2000);
                return 0;
              }
            }
          }
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isActive, phase, cycle, onComplete]);

  const startBreathing = () => {
    setIsActive(true);
    setPhase('inhale');
    setCount(phaseDurations.inhale);
    setCycle(1);
  };

  const getPhaseText = () => {
    switch (phase) {
      case 'inhale':
        return '천천히 깊게 들이마시세요';
      case 'hold':
        return '숨을 멈추고 잠시 기다려주세요';
      case 'exhale':
        return '천천히 길게 내쉬세요';
      default:
        return '';
    }
  };

  const getPhaseIcon = () => {
    switch (phase) {
      case 'inhale':
        return '🌬️';
      case 'hold':
        return '⏸️';
      case 'exhale':
        return '💨';
      default:
        return '🌬️';
    }
  };

  if (isCompleted) {
    return (
      <div className="text-center">
        <div className="text-5xl mb-5">🎉</div>
        <h2 className="text-xl font-bold text-gray-900 mb-3">
          심호흡 완료!
        </h2>
        <p className="text-sm text-gray-600 mb-5">
          {emotion}을 심호흡으로 해소했습니다
        </p>
        <div className="bg-green-50 rounded-2xl p-5 mb-5">
          <h3 className="text-base font-semibold mb-3">4-7-8 호흡법 완료</h3>
          <div className="grid grid-cols-2 gap-3 text-center">
            <div>
              <div className="text-xl font-bold text-mint">{totalCycles}</div>
              <div className="text-xs text-gray-600">완료한 사이클</div>
            </div>
            <div>
              <div className="text-xl font-bold text-mint">{emotion}</div>
              <div className="text-xs text-gray-600">감정</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="text-center">
      <div className="text-5xl mb-5">{getPhaseIcon()}</div>
      <h2 className="text-xl font-bold text-gray-900 mb-3">
        심호흡 운동
      </h2>
      <p className="text-sm text-gray-600 mb-5">
        4-7-8 호흡법으로 마음을 가라앉혀보세요
      </p>

      {!isActive ? (
        <div className="space-y-5">
          <div className="bg-blue-50 rounded-2xl p-5">
            <h3 className="text-base font-semibold mb-3">4-7-8 호흡법</h3>
            <div className="space-y-1.5 text-xs text-gray-600">
              <p>• 4초 들이쉬기</p>
              <p>• 7초 숨 멈추기</p>
              <p>• 8초 내쉬기</p>
              <p>• 총 5회 반복</p>
            </div>
          </div>
          <button
            onClick={startBreathing}
            className="btn-primary px-6 py-2.5"
          >
            심호흡 시작
          </button>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="text-3xl font-bold text-mint mb-3">
            {count}
          </div>
          <div className="text-base text-gray-700 mb-3">
            {getPhaseText()}
          </div>
          <div className="bg-blue-50 rounded-2xl p-3">
            <p className="text-xs text-gray-600">
              사이클: {cycle}/{totalCycles}
            </p>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div 
              className="bg-mint h-1.5 rounded-full transition-all duration-1000"
              style={{ 
                width: `${((cycle - 1) * 19 + (phase === 'inhale' ? 0 : phase === 'hold' ? 4 : 11)) / (totalCycles * 19) * 100}%` 
              }}
            ></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BreathingActivity; 